import {
  ButtonType,
  ButtonWidget,
  DropdownButtonType,
  List,
  ListItem,
  SingleSelectDropdown,
  TextFieldWidget,
  TextLinkWidget
} from '@tableau/widgets';
import { DataGrid } from '@tableau/widgets-datagrid';
import { FormikProps, useFormikContext } from 'formik';
import React from 'react';

import { FormikCheckbox } from '~commonInfra/components/FormikCheckbox';
import { HelpService } from '~commonInfra/services/HelpService';
import { SessionServerSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { learnMoreStyle, SettingsStyles } from '~src/settings/styles/CommonStyles';
import { ExtensionSafeListEntry, IDashExtSiteSettings } from '~src/settings/types/ISiteSettingsForSiteAdmin';
import { Sites } from '~src/siteManagement/services/resources/Sites';

import { Messages } from '../../../codegen/Localize';
import { SettingsSection } from '../../components/common/SettingsSection';
import { componentRegistryEntry } from '../../general-settings/components/SettingsComponentRegistryFunctions';
import { wideField } from '../../general-settings/styles/site-name-and-id/SiteNameAndIdStyles';
import { useSelector } from '../../general-settings/utils';
import { ExtensionsSettingsSubFormikProps } from '../ExtensionsSettingsCoordinator';
import { ExtensionsSettingsSubspaceState } from '../ExtensionsSettingsSubspaceShared';
import {
  addButtonInfoStyle,
  addButtonStyle,
  buttonStyle,
  dataGridContainerStyle,
  dataGridRowHeight,
  dataGridSafeContainerStyle,
  dropDownStyle,
  sectionHeader,
  textFieldStyle
} from './ExtensionsSectionStyle';
import {
  ADD_SAFE_URL_BUTTON_TEST_ID,
  ADD_SAFE_URL_TOOLTIP_TEST_ID,
  DELETE_SAFE_URL_BUTTON_PREFIX_TEST_ID,
  EXTENSIONS_DISABLED_WARNING_TEST_ID,
  FULL_DATA_PREFIX_TEST_ID,
  FULL_DATA_TOOLTIP_TEST_ID,
  INCLUDE_PARTNER_BUILT_EXTENSIONS_TEST_ID,
  INCLUDE_SANDBOXED_EXTENSIONS_TEST_ID,
  INCLUDE_TABLEAU_BUILT_EXTENSIONS_TEST_ID,
  SAFE_URL_INPUT_PREFIX_TEST_ID,
  SAFELIST_EMPTY_TEST_ID,
  SANDBOXED_EXTENSIONS_ENABLED_TEST_ID,
  SITE_EXTENSIONS_ENABLED_TEST_ID,
  SITE_EXTENSIONS_LEARN_MORE_LINK_TEST_ID,
  SITE_EXTENSIONS_SECTION_TEST_ID,
  TRUSTED_EXTENSIONS_ENABLED_TEST_ID,
  USER_PROMPT_PREFIX_TEST_ID,
  USER_PROMPT_TOOLTIP_TEST_ID
} from './ExtensionsTestIds';
import { QuickTooltip } from './QuickTooltip';

export const EXTENSIONS_ENABLED_FIELD = 'settings.dashExtSiteSettings.extensionsEnabled';
export const SANDBOXED_EXTENSIONS_ENABLED_FIELD = 'settings.dashExtSiteSettings.allowSandboxed';
export const TRUSTED_EXTENSIONS_ENABLED_FIELD = 'settings.dashExtSiteSettings.allowTrusted';
export const INCLUDE_SANDBOXED_EXTENSIONS_FIELD = 'settings.dashExtSiteSettings.includeSandboxed';
export const INCLUDE_TABLEAU_BUILT_EXTENSIONS_FIELD = 'settings.dashExtSiteSettings.includeTableauBuilt';
export const INCLUDE_PARTNER_BUILT_EXTENSIONS_FIELD = 'settings.dashExtSiteSettings.includePartnerBuilt';

const DEFAULT_URL = 'https://';
const DEFAULT_FULL_DATA_ACCESS = false;
const DEFAULT_USER_PROMPT = true;

interface DropDownOption {
  label: string;
  value: string;
}

export function ExtensionsSiteSection(props: ExtensionsSettingsSubFormikProps) {
  const formik = useFormikContext<ExtensionsSettingsSubFormikProps>();
  const extensionsEnabled = formik.values.settings.dashExtSiteSettings.extensionsEnabled;
  const allowTrusted = formik.values.settings.dashExtSiteSettings.allowTrusted;

  return (
    <SettingsSection title={useFullSiteContent() ? Messages.extensionsSettings_header() : ''}
                     testId={SITE_EXTENSIONS_SECTION_TEST_ID}
                     description={useFullSiteContent() ? <SettingsSectionDescription/> : ''}>

      {!props.settings.dashExtServerSettings.extensionsGloballyEnabled &&
        <div data-tb-test-id={EXTENSIONS_DISABLED_WARNING_TEST_ID} className={'tb-warning-text tb-settings-group-name'}>
          {Messages.extensionsSiteSettings_disabledOnServerMessage()}
        </div>
      }

      <fieldset className="tb-settings-control-grouping" aria-label={Messages.extensionsSettings_permissionsHeader()}>

        {useFullSiteContent() &&
          <FormikCheckbox name={EXTENSIONS_ENABLED_FIELD}
                          label={Messages.extensionsSiteSettings_allowForSite()}
                          testId={SITE_EXTENSIONS_ENABLED_TEST_ID}>
          </FormikCheckbox>
        }
        <AllowExtensionsByCategory allDisabled={!extensionsEnabled} optionsDisabled={!allowTrusted} />
      </fieldset>


      <h3 className={sectionHeader}>{Messages.extensionsSiteSettings_whiteListHeader()}</h3>
      <SafeList formik={formik} disabled={!extensionsEnabled} />
    </SettingsSection>
  );
}

function SettingsSectionDescription() {
  return (
    <div style={{whiteSpace: 'pre-wrap'}}>
      {useSelectDescriptionByServerEnvironment()}
      <TextLinkWidget testId={SITE_EXTENSIONS_LEARN_MORE_LINK_TEST_ID}
                      target={'_blank'}
                      className={learnMoreStyle}
                      href={HelpService.getServerHelpUrlByCategory('extensions')}>
        {Messages.learnMore()}
      </TextLinkWidget>
    </div>
  );
}

function AllowExtensionsByCategory(props: { allDisabled: boolean, optionsDisabled: boolean }) {
  const {allDisabled, optionsDisabled} = props;
  const enableTrustedExtensionsLabel = useSelectAllowTrustedByServerEnvironment();
  const enableSandboxExtensionsLabel = useSelectAllowSandboxByServerEnvironment();

  if (FeatureFlags.vizExtensionsTrustedTableau || FeatureFlags.vizExtensionsTrustedPartner) {
    return (
    <FormikCheckbox name={TRUSTED_EXTENSIONS_ENABLED_FIELD}
                    label={enableTrustedExtensionsLabel}
                    testId={TRUSTED_EXTENSIONS_ENABLED_TEST_ID}
                    alwaysShowChildren={true}
                    disabled={allDisabled}>
      <div className={SettingsStyles.checkboxGroup}>
        <div>
          <FormikCheckbox name={INCLUDE_SANDBOXED_EXTENSIONS_FIELD}
                          label={Messages.extensionsSettings_includeSandboxedExtensions()}
                          testId={INCLUDE_SANDBOXED_EXTENSIONS_TEST_ID}
                          disabled={allDisabled || optionsDisabled}>
          </FormikCheckbox>
          {FeatureFlags.vizExtensionsTrustedTableau && <FormikCheckbox name={INCLUDE_TABLEAU_BUILT_EXTENSIONS_FIELD}
                          label={Messages.extensionsSettings_includeTableauBuiltExtensions()}
                          testId={INCLUDE_TABLEAU_BUILT_EXTENSIONS_TEST_ID}
                          disabled={allDisabled || optionsDisabled}>
          </FormikCheckbox>}
          {FeatureFlags.vizExtensionsTrustedPartner && <FormikCheckbox name={INCLUDE_PARTNER_BUILT_EXTENSIONS_FIELD}
                          label={Messages.extensionsSettings_includePartnerBuiltExtensions()}
                          testId={INCLUDE_PARTNER_BUILT_EXTENSIONS_TEST_ID}
                          disabled={allDisabled || optionsDisabled}>
          </FormikCheckbox>}
        </div>
      </div>
    </FormikCheckbox>
    );
  }

  return(
    <FormikCheckbox name={SANDBOXED_EXTENSIONS_ENABLED_FIELD}
                    label={enableSandboxExtensionsLabel}
                    testId={SANDBOXED_EXTENSIONS_ENABLED_TEST_ID}
                    disabled={allDisabled}>
    </FormikCheckbox>
  );
}

function getSafeList(formik: FormikProps<ExtensionsSettingsSubFormikProps>) {
  return formik.values.settings.dashExtSiteSettings.safeList;
}

function setSafeList(formik: FormikProps<ExtensionsSettingsSubFormikProps>, safeList: ExtensionSafeListEntry[]) {
  formik.setFieldValue('settings.dashExtSiteSettings.safeList', safeList, false);
}

function handleUrlChange(idx: number, formik: FormikProps<ExtensionsSettingsSubFormikProps>, event: React.ChangeEvent<HTMLInputElement>) {
  const safeList = [...getSafeList(formik)];
  safeList[idx] = {...safeList[idx], url: event.target.value};
  setSafeList(formik, safeList);
}

function handleFullDataOptionChange(idx: number, formik: FormikProps<ExtensionsSettingsSubFormikProps>, value: string) {
  const safeList = [...getSafeList(formik)];
  safeList[idx] = {...safeList[idx], fullDataAllowed: value === 'true'};
  setSafeList(formik, safeList);
}

function handlePromptNeededOptionChange(idx: number, formik: FormikProps<ExtensionsSettingsSubFormikProps>, value: string) {
  const safeList = [...getSafeList(formik)];
  safeList[idx] = {...safeList[idx], promptNeeded: value === 'true'};
  setSafeList(formik, safeList);
}

export function focusOnAddButton() {
  var querySelector: string = 'button.' + ADD_SAFE_URL_BUTTON_TEST_ID;
  const buttonToFocus = document.querySelector<HTMLElement>(querySelector);
  if (buttonToFocus !== null){
    buttonToFocus.focus();
  }
}

export function handleDeleteClient(idx: number, formik: FormikProps<ExtensionsSettingsSubFormikProps>) {
  if (idx > -1) {
    const safeList = [...getSafeList(formik)];
    safeList.splice(idx, 1);
    setSafeList(formik, safeList);
    if (idx === 0 && safeList.length === 0){
      // no rows left in the table
      focusOnAddButton();
    }
  }
}

export function handleAddClient(formik: FormikProps<ExtensionsSettingsSubFormikProps>) {
  const safeList = [...getSafeList(formik)];
  safeList.unshift({
    url: DEFAULT_URL,
    fullDataAllowed: DEFAULT_FULL_DATA_ACCESS,
    promptNeeded: DEFAULT_USER_PROMPT
  });
  setSafeList(formik, safeList);
}

function useFullSiteContent() {
  return useSelector<boolean>(SessionServerSelectors.doesTheServerHaveMoreThanOneSite);
}

function useSelectDescriptionByServerEnvironment() {
  const isTrustedFFsON = FeatureFlags.vizExtensionsTrustedTableau || FeatureFlags.vizExtensionsTrustedPartner;
  return useSelector<boolean>(SessionServerSelectors.isOnlineEnvironment)
    ? (isTrustedFFsON ? Messages.extensionsSiteSettings_descriptionWhat_online_TrustedFFsON() : Messages.extensionsSiteSettings_descriptionWhat_online_TrustedFFsOFF())
    : (isTrustedFFsON ? Messages.extensionsSiteSettings_descriptionWhat_server_TrustedFFsON() : Messages.extensionsSiteSettings_descriptionWhat_server_TrustedFFsOFF());
}

function useSelectAllowSandboxByServerEnvironment() {
  return useSelector<boolean>(SessionServerSelectors.isOnlineEnvironment)
    ? Messages.extensionsSettings_allowSandboxedExtensions_online()
    : Messages.extensionsSettings_allowSandboxedExtensions_server();
  }

function useSelectAllowTrustedByServerEnvironment() {
  return useSelector<boolean>(SessionServerSelectors.isOnlineEnvironment)
    ? Messages.extensionsSettings_allowTrustedExtensions_online()
    : Messages.extensionsSettings_allowTrustedExtensions_server();
}

function SafeList(props: { formik: FormikProps<ExtensionsSettingsSubFormikProps>, disabled: boolean }) {
  const {formik, disabled} = props;
  const FullDataOptions: DropDownOption[] = [
    {label: Messages.extensionsSiteSettings_whitelist_FullDataAccess_Allow(), value: 'true'},
    {label: Messages.extensionsSiteSettings_whitelist_FullDataAccess_Deny(), value: 'false'}
  ];

  const PromptNeededOptions: DropDownOption[] = [
    {label: Messages.extensionsSiteSettings_whitelist_UserPrompts_Show(), value: 'true'},
    {label: Messages.extensionsSiteSettings_whitelist_UserPrompts_Hide(), value: 'false'}
  ];

  const fullDataList: ListItem[] = FullDataOptions.map((option: DropDownOption) => {
    const listItem: ListItem = {
      content: option.label,
      value: option.value
    };
    return listItem;
  });

  const promptNeededList: ListItem[] = PromptNeededOptions.map((option: DropDownOption) => {
    const listItem: ListItem = {
      content: option.label,
      value: option.value
    };
    return listItem;
  });

  enum SafeListDataKeys {
    SafeUrl = 'safeUrl',
    FullData = 'fullData',
    UserPrompt = 'userPrompt',
  }

  const columns: DataGrid.Column[] = [];
  columns.push({
    name: SafeListDataKeys.SafeUrl,
    dataKey: SafeListDataKeys.SafeUrl,
    headerText: Messages.extensionsSettings_list_URLHeader(),
    headerFormatter: (_) => {
      return <span className={addButtonInfoStyle}>
        {Messages.extensionsSettings_list_URLHeader()}
        <ButtonWidget buttonType={ButtonType.Outline}
          testId={ADD_SAFE_URL_BUTTON_TEST_ID}
          className={`${buttonStyle} ${addButtonStyle} ${ADD_SAFE_URL_BUTTON_TEST_ID}`}
          onClick={() => handleAddClient(formik) }
          disabled={disabled}
          type='button'>{Messages.extensionsSettings_addUrlButton()}
        </ButtonWidget>
        <QuickTooltip
          testId={ADD_SAFE_URL_TOOLTIP_TEST_ID}
          label={Messages.extensionsSettings_list_URLHeader()}
          content={Messages.extensionsSiteSettings_whitelist_URLHeaderInfo()} />
      </span>;
    },
    growToFit: true,
    resizable: false,
    sortable: false,
    cellFormatter: (formatterProps) => {
      return <TextFieldWidget testId={`${SAFE_URL_INPUT_PREFIX_TEST_ID}${formatterProps.rowIdx}`}
        className={`${wideField} ${textFieldStyle}`}
        value={formatterProps.row.data[SafeListDataKeys.SafeUrl]}
        required={true}
        label={null}
        aria-label={Messages.extensionsSettings_list_URLHeader()}
        onChange={(event) => handleUrlChange(formatterProps.rowIdx, formik, event)}
        disabled={disabled}
      />;
    },
  });
  columns.push({
    name: SafeListDataKeys.FullData,
    dataKey: SafeListDataKeys.FullData,
    headerText: '',
    headerFormatter: (_) => {
      return <span>
        {Messages.extensionsSiteSettings_whitelist_FullDataAccessHeader()}
        <QuickTooltip
          testId={FULL_DATA_TOOLTIP_TEST_ID}
          label={Messages.extensionsSiteSettings_whitelist_FullDataAccessHeader()}
          content={Messages.extensionsSiteSettings_whitelist_FullDataInfo()} />
      </span>;
    },
    resizable: false,
    growToFit: true,
    sortable: false,
    cellFormatter: (formatterProps) => {
      return <SingleSelectDropdown
        className={dropDownStyle}
        testId={`${FULL_DATA_PREFIX_TEST_ID}${formatterProps.rowIdx}`}
        list={<List items={fullDataList} />}
        value={formatterProps.row.data[SafeListDataKeys.FullData] ? 'true' : 'false'}
        type={DropdownButtonType.Outline}
        onChange={(value) => handleFullDataOptionChange(formatterProps.rowIdx, formik, value)}
        label={null}
        aria-label={Messages.extensionsSiteSettings_whitelist_FullDataAccessHeader()}
        disabled={disabled}
      />;
    },
  });
  columns.push({
    name: SafeListDataKeys.UserPrompt,
    dataKey: SafeListDataKeys.UserPrompt,
    headerText: '',
    headerFormatter: (_) => {
      return <span>
        {Messages.extensionsSiteSettings_whitelist_UserPromptsHeader()}
        <QuickTooltip
          testId={USER_PROMPT_TOOLTIP_TEST_ID}
          label={Messages.extensionsSiteSettings_whitelist_UserPromptsHeader()}
          content={Messages.extensionsSiteSettings_whitelist_UserPromptsInfo()} />
      </span>;
    },
    resizable: false,
    growToFit: true,
    sortable: false,
    cellFormatter: (formatterProps) => {
      return <SingleSelectDropdown
        className={dropDownStyle}
        testId={`${USER_PROMPT_PREFIX_TEST_ID}${formatterProps.rowIdx}`}
        list={<List items={promptNeededList} />}
        value={formatterProps.row.data[SafeListDataKeys.UserPrompt] ? 'true' : 'false'}
        type={DropdownButtonType.Outline}
        onChange={(value) => handlePromptNeededOptionChange(formatterProps.rowIdx, formik, value)}
        label={null}
        aria-label={Messages.extensionsSiteSettings_whitelist_UserPromptsHeader()}
        disabled={disabled}
      />;
    },
  });
  columns.push({
    name: '',
    dataKey: '',
    headerText: '',
    resizable: false,
    sortable: false,
    cellFormatter: (formatterProps) => {
      return <ButtonWidget buttonType={ButtonType.LowEmphasis}
        testId={`${DELETE_SAFE_URL_BUTTON_PREFIX_TEST_ID}${formatterProps.rowIdx}`}
        className={buttonStyle}
        onClick={() => handleDeleteClient(formatterProps.rowIdx, formik)}
        disabled={disabled}
        type='button'>{Messages.deleteMessage()}
      </ButtonWidget>;
    },
  });

  let rows: DataGrid.Row[] = [];
  let rowIndex = 0;
  getSafeList(formik).forEach(rowData => {
    let rowItemData: { [key: string]: {} } = {};
    rowItemData[SafeListDataKeys.SafeUrl] = rowData.url;
    rowItemData[SafeListDataKeys.FullData] = rowData.fullDataAllowed;
    rowItemData[SafeListDataKeys.UserPrompt] = rowData.promptNeeded;
    rows.push({
      key: rowIndex.toString(),
      data: rowItemData
    });
    rowIndex++;
  });

  return <div>
    <div className={`tb-spaces-list-view ${dataGridContainerStyle} ${dataGridSafeContainerStyle}`}>
      <DataGrid
        config= {{
          rowOuterHeight: dataGridRowHeight,
          rememberLastScrollPos: true
        }}
        emptyChild={
          <div data-tb-test-id={SAFELIST_EMPTY_TEST_ID} className="tb-data-grid-cell-text tb-data-grid-bridge-center">
            {Messages.extensionsSiteSettings_whitelist_emptyMessage()}
          </div>
        }
        cols={columns}
        rowStore={{
          getItems: (start: number, end: number, rowHandler: (rows: DataGrid.Row[]) => void) => {
            rowHandler(rows);
          }
        }}
        showVerticalGridLines={false}
        showHorizontalGridLines={false}
        showRowBanding={true}
      />
    </div>
  </div>;
}

export const ExtensionsSiteSectionSpecs = componentRegistryEntry({
  name: 'ExtensionsSiteSection',
  component: ExtensionsSiteSection,
  onSubmit: async (settings: ExtensionsSettingsSubspaceState) => {
    //  @W-13493904: explicitly make sure only the intended values are being passed along
    let updateValues: IDashExtSiteSettings = {
      extensionsEnabled: settings.dashExtSiteSettings.extensionsEnabled,
      allowSandboxed: settings.dashExtSiteSettings.allowSandboxed,
      allowTrusted: settings.dashExtSiteSettings.allowTrusted,
      includeSandboxed: settings.dashExtSiteSettings.includeSandboxed,
      includeTableauBuilt: settings.dashExtSiteSettings.includeTableauBuilt,
      includePartnerBuilt: settings.dashExtSiteSettings.includePartnerBuilt,
      safeList: settings.dashExtSiteSettings.safeList
    };
    await Promise.all([Sites.instance.updateCurrentExtensionsSiteSettingsForSiteAdmin(updateValues)]);
  },
});
